import styles from './index.scss';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classnames from 'classnames/bind';
import LoginBasicBox from '@/components/auth/login-basic-box/login-basic-box';
import { Button, Form } from 'react-bootstrap';
import { useRecoveryPasswordResetMutation } from '@/services/auth.api';
import { mapRequestError } from '@/services/error.utils';
import PasswordInput from '@/components/PasswordInput';
import * as yup from 'yup';
import { PASSWORD_REGEX } from '@/constants/system';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import _ from 'lodash';
import { dispatch } from '@/store';
import { addToast, ToastType } from '@/store/system';
import PasswordStrengthIndicator from '@/components/password-strength-indicator/password-strength-indicator';
import ResetPassWordSuccess from '../ResetPassWordSuccess';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';

const cx = classnames.bind(styles);

type PasswordForm = {
  pass: string;
  confirmPass: string;
};

const defaultFormValue: PasswordForm = {
  pass: '',
  confirmPass: '',
};

const ResetPass = () => {
  const navigate = useNavigate();
  const { lt } = useLevvelTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPass, setCurrentPass] = useState('');
  const [searchParams] = useSearchParams();
  const [isSuccess, setIsSuccess] = useState(false);

  const [resetPass, { isLoading }] = useRecoveryPasswordResetMutation();

  const schema = yup
    .object({
      pass: yup
        .string()
        .trim()
        .required(
          `${lt('cdb_profile_new_password')} ${lt('cdb_admin_required_text')}`
        )
        .matches(PASSWORD_REGEX, lt('cdb_admin_form_verification_pwd_format')),
      confirmPass: yup
        .string()
        .trim()
        .oneOf(
          [yup.ref('pass'), null],
          lt('cdb_admin_form_verification_pwd_match')
        )
        .required(
          `${lt('cdb_profile_confirm_password_text')} ${lt(
            'cdb_admin_required_text'
          )}`
        ),
    })
    .required();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm<PasswordForm>({
    resolver: yupResolver(schema),
    defaultValues: defaultFormValue,
  });

  // ADR-2: the emailed link is `?token=<base64url of 32 random bytes>&exp=<unix
  // seconds>`. The token is OPAQUE — it encodes nothing the client can read (the
  // account name deliberately left the payload) so it is only ever passed through.
  const token = searchParams.get('token');
  const exp = searchParams.get('exp');

  const goToLinkExpired = () =>
    navigate('/auth/link-expired', { replace: true });

  const submitForm = (formData: PasswordForm) => {
    resetPass({
      token: token?.trim() ?? '',
      newPassword: formData.pass?.trim(),
    })
      .unwrap()
      .then(() => {
        // The response body is empty now, so there is no `message` to echo.
        // `ReSetSuccessfully` is the i18n key the legacy BFF returned in that
        // field (resetpwd.go:93) — same wording, sourced locally.
        dispatch(
          addToast({
            msg: lt('ReSetSuccessfully'),
            title: (
              <div className="letter-space3">
                {lt('cdb_message_status_done_text')}
              </div>
            ) as any,
            type: ToastType.Success,
          })
        );
        setIsSuccess(true);
      })
      .catch(error => {
        // 410 = the token is gone: already spent, or its Redis TTL lapsed. This
        // is the server's verdict and the only authoritative one — send the user
        // to the expired-link page. A 422 (policy) deliberately does NOT land
        // here: the server checks policy BEFORE consuming, so the link survives
        // and the user can retry on this same form.
        if (error?.status === 410) {
          goToLinkExpired();
          return;
        }
        const { i18nKey, text } = mapRequestError(error);
        dispatch(
          addToast({
            msg: i18nKey ? lt(i18nKey) : text ?? '',
            title: (
              <div className="letter-space3">
                {lt('cdb_message_status_error_text')}
              </div>
            ) as any,
            type: ToastType.Danger,
          })
        );
      });
  };

  const detectPasswordValidity = (isSubmitting: boolean) => {
    setIsSubmitting(isSubmitting);
    setCurrentPass(getValues('pass'));
  };

  // The client-side expiry decode is GONE (ADR-2). It used to
  // JSON.parse(atob(signature)) to read `ExpiredAt`, which only worked because the
  // legacy signature was base64 JSON — that readability was the vulnerability
  // (`(account, expiry-second)` was guessable inside the 5-minute window). Against
  // an opaque random token the parse throws, and the old catch treated a throw as
  // "expired", so keeping it would have marked EVERY link expired.
  //
  // Expiry is now the server's call (the Redis TTL), surfaced as 410 on submit.
  // `exp` is a plaintext UX hint that preserves today's "click a stale link, find
  // out immediately" experience instead of making the user fill the form first.
  // It is never trusted for anything else: tampering with it upward only costs the
  // user a wasted form, and the real check still runs server-side.
  React.useEffect(() => {
    const expiredByHint = !!exp && Number(exp) * 1000 < Date.now();
    if (!token || expiredByHint) {
      goToLinkExpired();
    }
  }, []);

  return (
    <LoginBasicBox isResetBoxMobileSize={true}>
      <div className={cx([styles.container])}>
        {isSuccess ? (
          <ResetPassWordSuccess />
        ) : (
          <main className={cx([styles.main])}>
            <section className={cx([styles.info])}>
              <section className={cx([styles.title])}>
                <span className="title-bold">
                  {lt('cdb_forgot_password_reset_title')}
                </span>
              </section>
              <Form noValidate validated={!_.isEmpty(errors)}>
                <Form.Group className={cx(['mb-4'])} controlId="pass">
                  <Controller
                    name="pass"
                    control={control}
                    render={({ field }) => (
                      <PasswordInput
                        {...field}
                        onKeyUp={() => {
                          detectPasswordValidity(false);
                        }}
                        className={cx([styles.formRowInput])}
                        placeholder={lt('cdb_profile_new_password')}
                        isInvalid={!!errors.pass}
                      />
                    )}
                  />
                  {errors.pass && (
                    <Form.Control.Feedback
                      type="invalid"
                      className={cx(['invalid-feedback'])}>
                      <span>{errors?.pass.message}</span>
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                {currentPass && (
                  <section className={cx([styles.passwordFactors])}>
                    <div className={cx(['mb-3', 'createPasswordText'])}>
                      {lt('auth_reset_create_password_text')}
                    </div>
                    <PasswordStrengthIndicator
                      password={currentPass}
                      showError={isSubmitting}
                    />
                  </section>
                )}
                <Form.Group controlId="confirmPass" className="mb-4">
                  <Controller
                    name="confirmPass"
                    control={control}
                    render={({ field }) => (
                      <PasswordInput
                        {...field}
                        className={cx([styles.formRowInput])}
                        placeholder={lt('cdb_confirm_new_password')}
                        isInvalid={!!errors.confirmPass}
                      />
                    )}
                  />
                  {errors.confirmPass && (
                    <Form.Control.Feedback
                      type="invalid"
                      className={cx(['invalid-feedback'])}>
                      <span>{errors?.confirmPass.message}</span>
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Form>
            </section>
            <section className={cx([styles.actions])}>
              <Button
                onClick={e => {
                  handleSubmit(submitForm)(e);
                  detectPasswordValidity(true);
                }}
                className={cx(['title-semiBold', styles.loginButton])}>
                {isLoading && (
                  <span>
                    <span
                      className="spinner-border spinner-border"
                      role="status"
                      aria-hidden="true"
                    />
                    &nbsp;&nbsp;
                  </span>
                )}
                {lt('cdb_forgot_password_reset')}
              </Button>
            </section>
          </main>
        )}
      </div>
    </LoginBasicBox>
  );
};

export default ResetPass;
